import { Modal } from '@/components/admin/Modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/cn';
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { ResponsePromptTreeNode } from '@innermate/shared';
import {
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Copy,
  FilePlus,
  FileText,
  Folder,
  FolderOpen,
  FolderPlus,
  GripVertical,
  Pencil,
  Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

interface PromptTreeProps {
  tree: ResponsePromptTreeNode[];
  selectedFileId: string | null;
  onSelect: (id: string) => void;
  onCreate: (parentId: string | null, type: 'folder' | 'file') => void;
  onRename: (id: string) => void;
  onDelete: (id: string) => void;
  /** 파일 복제 — folder 에서는 호출 안 됨. */
  onDuplicate?: (id: string) => void;
  /** 노드 이동. targetIndex 는 새 parent 안 0-based 위치. 큰 값이면 맨 뒤. */
  onMove?: (id: string, newParentId: string | null, targetIndex: number) => void;
}

/** 부모 id → 자식 배열 (현재 visible 트리 안에서) 의 인덱스 룩업. */
function buildParentIndex(
  tree: ResponsePromptTreeNode[],
): Map<string | null, ResponsePromptTreeNode[]> {
  const map = new Map<string | null, ResponsePromptTreeNode[]>();
  const walk = (nodes: ResponsePromptTreeNode[], parent: string | null) => {
    map.set(parent, nodes);
    for (const n of nodes) walk(n.children, n.id);
  };
  walk(tree, null);
  return map;
}

interface RowProps extends Omit<PromptTreeProps, 'tree' | 'onMove'> {
  node: ResponsePromptTreeNode;
  depth: number;
  expanded: Set<string>;
  setExpanded: (next: Set<string>) => void;
  filterMatch: (n: ResponsePromptTreeNode) => boolean;
  filterActive: boolean;
  /** drag 중인 노드 id (자기 자신/자손에 drop 금지 판정용). */
  draggingId: string | null;
  /** 현재 키보드 포커스 행 id — 시각 ring 표시용. */
  focusedId: string | null;
}

function collectDescendantIds(node: ResponsePromptTreeNode, acc: Set<string>): void {
  acc.add(node.id);
  for (const c of node.children) collectDescendantIds(c, acc);
}

function isAncestorMatch(
  node: ResponsePromptTreeNode,
  fn: (n: ResponsePromptTreeNode) => boolean,
): boolean {
  if (fn(node)) return true;
  return node.children.some((c) => isAncestorMatch(c, fn));
}

function TreeRow({
  node,
  depth,
  selectedFileId,
  onSelect,
  onCreate,
  onRename,
  onDelete,
  onDuplicate,
  expanded,
  setExpanded,
  filterMatch,
  filterActive,
  draggingId,
  focusedId,
}: RowProps) {
  if (filterActive && !isAncestorMatch(node, filterMatch)) return null;
  const isOpen = expanded.has(node.id);
  const isFolder = node.type === 'folder';
  const isSelected = !isFolder && node.id === selectedFileId;
  const isFocused = node.id === focusedId;

  // 드래그 핸들만 draggable. row 클릭은 그대로 선택 동작.
  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
  } = useDraggable({
    id: node.id,
  });

  // drop 금지 판정: 자기 자신 / 자손에는 drop 불가 (자식 폴더로 이동 시 cycle 형성).
  const blocksDrop = useMemo(() => {
    if (!draggingId) return true;
    if (draggingId === node.id) return true;
    const descSet = new Set<string>();
    collectDescendantIds(node, descSet);
    return descSet.has(draggingId);
  }, [draggingId, node]);

  // 3-zone (folder) / 2-zone (file) 의 droppable strip.
  // 각 strip 의 hit area 는 absolute 로 row 위에 겹쳐 깔리고, 시각 indicator 는 별도 div.
  const beforeDrop = useDroppable({ id: `before:${node.id}`, disabled: blocksDrop });
  const intoDrop = useDroppable({ id: `into:${node.id}`, disabled: blocksDrop || !isFolder });
  const afterDrop = useDroppable({ id: `after:${node.id}`, disabled: blocksDrop });

  const toggle = () => {
    const next = new Set(expanded);
    if (isOpen) next.delete(node.id);
    else next.add(node.id);
    setExpanded(next);
  };

  return (
    <div className="relative">
      {/* drop indicator — 위 라인 */}
      {beforeDrop.isOver && !blocksDrop ? (
        <div
          className="absolute left-0 right-0 top-0 z-10 h-0.5 bg-accent pointer-events-none"
          style={{ marginLeft: 6 + depth * 14 }}
        />
      ) : null}
      {/* drop indicator — 아래 라인 */}
      {afterDrop.isOver && !blocksDrop ? (
        <div
          className="absolute left-0 right-0 bottom-0 z-10 h-0.5 bg-accent pointer-events-none"
          style={{ marginLeft: 6 + depth * 14 }}
        />
      ) : null}
      <div
        role="treeitem"
        aria-selected={isSelected}
        aria-expanded={isFolder ? isOpen : undefined}
        className={cn(
          'group flex h-8 items-center gap-1 rounded px-1.5 text-sm relative',
          isSelected ? 'bg-accent/15 text-accent' : 'hover:bg-surface-muted/60 text-foreground',
          isFocused && !intoDrop.isOver ? 'ring-1 ring-accent/60 ring-inset' : '',
          intoDrop.isOver && !blocksDrop && isFolder
            ? 'ring-2 ring-accent ring-inset bg-accent/10'
            : '',
        )}
        style={{ paddingLeft: 6 + depth * 14 }}
      >
        {/* 보이지 않는 drop zones — folder: 위 25%/가운데 50%/아래 25%, file: 위 50%/아래 50% */}
        {draggingId && !blocksDrop ? (
          <>
            <div
              ref={beforeDrop.setNodeRef}
              className="absolute inset-x-0 top-0 z-5"
              style={{ height: isFolder ? '25%' : '50%' }}
            />
            {isFolder ? (
              <div
                ref={intoDrop.setNodeRef}
                className="absolute inset-x-0 z-5"
                style={{ top: '25%', height: '50%' }}
              />
            ) : null}
            <div
              ref={afterDrop.setNodeRef}
              className="absolute inset-x-0 bottom-0 z-5"
              style={{ height: isFolder ? '25%' : '50%' }}
            />
          </>
        ) : null}
        <button
          ref={setDragRef}
          type="button"
          {...listeners}
          {...attributes}
          aria-label="드래그하여 이동"
          className="shrink-0 cursor-grab text-foreground-muted opacity-0 group-hover:opacity-100 hover:text-foreground active:cursor-grabbing"
        >
          <GripVertical className="size-3.5" />
        </button>
        {isFolder ? (
          <button type="button" onClick={toggle} className="text-foreground-muted shrink-0">
            {isOpen ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
          </button>
        ) : (
          <span className="w-3.5 shrink-0" />
        )}
        <button
          type="button"
          onClick={() => (isFolder ? toggle() : onSelect(node.id))}
          className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
        >
          {isFolder ? (
            isOpen ? (
              <FolderOpen className="size-4 text-accent shrink-0" />
            ) : (
              <Folder className="size-4 text-foreground-muted shrink-0" />
            )
          ) : (
            <FileText className="size-3.5 text-foreground-muted shrink-0" />
          )}
          <span className="truncate">{node.name}</span>
        </button>
        <div className="hidden items-center gap-0.5 group-hover:flex">
          {isFolder ? (
            <>
              <button
                type="button"
                title="폴더 추가"
                onClick={() => onCreate(node.id, 'folder')}
                className="p-1 rounded hover:bg-surface text-foreground-muted hover:text-foreground"
              >
                <FolderPlus className="size-3.5" />
              </button>
              <button
                type="button"
                title="프롬프트 추가"
                onClick={() => onCreate(node.id, 'file')}
                className="p-1 rounded hover:bg-surface text-foreground-muted hover:text-foreground"
              >
                <FilePlus className="size-3.5" />
              </button>
            </>
          ) : null}
          {!isFolder && onDuplicate ? (
            <button
              type="button"
              title="복제"
              onClick={() => onDuplicate(node.id)}
              className="p-1 rounded hover:bg-surface text-foreground-muted hover:text-foreground"
            >
              <Copy className="size-3.5" />
            </button>
          ) : null}
          <button
            type="button"
            title="이름 변경"
            onClick={() => onRename(node.id)}
            className="p-1 rounded hover:bg-surface text-foreground-muted hover:text-foreground"
          >
            <Pencil className="size-3.5" />
          </button>
          <button
            type="button"
            title="삭제"
            onClick={() => onDelete(node.id)}
            className="p-1 rounded hover:bg-surface text-foreground-muted hover:text-destructive"
          >
            <Trash2 className="size-3.5" />
          </button>
        </div>
      </div>
      {isFolder && isOpen
        ? node.children.map((c) => (
            <TreeRow
              key={c.id}
              node={c}
              depth={depth + 1}
              selectedFileId={selectedFileId}
              onSelect={onSelect}
              onCreate={onCreate}
              onRename={onRename}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
              expanded={expanded}
              setExpanded={setExpanded}
              filterMatch={filterMatch}
              filterActive={filterActive}
              draggingId={draggingId}
              focusedId={focusedId}
            />
          ))
        : null}
    </div>
  );
}

/** 루트 영역 drop zone — 노드를 최상위로 이동시킬 때 사용. */
function RootDropZone({ draggingId }: { draggingId: string | null }) {
  const { isOver, setNodeRef } = useDroppable({
    id: '__root__',
    disabled: !draggingId,
  });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'mx-1 mb-1 rounded border border-dashed border-border px-2 py-1 text-center text-[10px] text-foreground-muted',
        isOver ? 'border-accent bg-accent/10 text-accent' : 'opacity-50',
      )}
    >
      여기로 끌어다 놓으면 최상위로 이동
    </div>
  );
}

function collectAllNodeIds(nodes: ResponsePromptTreeNode[], acc: Set<string>): void {
  for (const n of nodes) {
    acc.add(n.id);
    collectAllNodeIds(n.children, acc);
  }
}

function collectNewFolderIds(
  nodes: ResponsePromptTreeNode[],
  prevIds: Set<string>,
  acc: string[],
): void {
  for (const n of nodes) {
    if (n.type === 'folder' && !prevIds.has(n.id)) acc.push(n.id);
    collectNewFolderIds(n.children, prevIds, acc);
  }
}

export function PromptTree(props: PromptTreeProps) {
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const init = new Set<string>();
    const collect = (nodes: ResponsePromptTreeNode[]) => {
      for (const n of nodes) {
        if (n.type === 'folder') {
          init.add(n.id);
          collect(n.children);
        }
      }
    };
    collect(props.tree);
    return init;
  });

  // 트리 갱신 시 새로 생긴 폴더는 자동 펼침. 기존에 사용자가 직접 접은 폴더는 보존.
  const prevAllIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const newFolderIds: string[] = [];
    collectNewFolderIds(props.tree, prevAllIdsRef.current, newFolderIds);
    if (newFolderIds.length > 0) {
      setExpanded((prev) => {
        const next = new Set(prev);
        for (const id of newFolderIds) next.add(id);
        return next;
      });
    }
    const allIds = new Set<string>();
    collectAllNodeIds(props.tree, allIds);
    prevAllIdsRef.current = allIds;
  }, [props.tree]);

  /** 트리 안의 모든 folder id — "전체 열기" 에 사용. */
  const allFolderIds = useMemo(() => {
    const ids: string[] = [];
    const walk = (nodes: ResponsePromptTreeNode[]) => {
      for (const n of nodes) {
        if (n.type === 'folder') {
          ids.push(n.id);
          walk(n.children);
        }
      }
    };
    walk(props.tree);
    return ids;
  }, [props.tree]);

  const expandAll = () => setExpanded(new Set(allFolderIds));
  const collapseAll = () => setExpanded(new Set());

  const [draggingId, setDraggingId] = useState<string | null>(null);

  // 4px 이동해야 드래그 시작 — row 클릭 (선택) 과 드래그 동작 분리.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const parentIndex = useMemo(() => buildParentIndex(props.tree), [props.tree]);

  const onDragEnd = (e: DragEndEvent) => {
    setDraggingId(null);
    if (!props.onMove) return;
    const activeId = String(e.active.id);
    if (!e.over) return;
    const overId = String(e.over.id);

    if (overId === '__root__') {
      // 루트 맨 뒤로
      const rootSiblings = parentIndex.get(null) ?? [];
      props.onMove(activeId, null, rootSiblings.length);
      return;
    }

    // `into:`, `before:`, `after:` prefix 파싱
    const colonIdx = overId.indexOf(':');
    if (colonIdx === -1) return;
    const zone = overId.slice(0, colonIdx) as 'into' | 'before' | 'after';
    const targetId = overId.slice(colonIdx + 1);
    if (targetId === activeId) return;

    if (zone === 'into') {
      // 폴더 안 맨 뒤로
      const folderChildren = parentIndex.get(targetId) ?? [];
      props.onMove(activeId, targetId, folderChildren.length);
      return;
    }

    // before/after: target 의 부모 안에서 target 위/아래로
    let targetParentId: string | null = null;
    let targetIndexInParent = -1;
    for (const [pid, siblings] of parentIndex) {
      const i = siblings.findIndex((s) => s.id === targetId);
      if (i !== -1) {
        targetParentId = pid;
        targetIndexInParent = i;
        break;
      }
    }
    if (targetIndexInParent === -1) return;

    // 같은 부모 안에서 자기 자신을 제외한 위치 계산이 필요.
    // 서버에서 같은 id 는 alegisthm 에서 자동 제외 후 clampedIndex 처리하므로 raw index 그대로 보냄.
    const insertAt = zone === 'before' ? targetIndexInParent : targetIndexInParent + 1;
    props.onMove(activeId, targetParentId, insertAt);
  };

  const filterActive = filter.trim().length > 0;
  const filterMatch = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (n: ResponsePromptTreeNode) => {
      if (n.name.toLowerCase().includes(q)) return true;
      if (n.type === 'file' && n.slug?.toLowerCase().includes(q)) return true;
      return false;
    };
  }, [filter]);

  // 검색 활성 시 매치된 노드 조상 자동 펼침.
  const effectiveExpanded = useMemo(() => {
    if (!filterActive) return expanded;
    const set = new Set(expanded);
    const walk = (n: ResponsePromptTreeNode): boolean => {
      const childMatch = n.children.some(walk);
      if (filterMatch(n) || childMatch) {
        if (n.type === 'folder') set.add(n.id);
        return true;
      }
      return false;
    };
    for (const n of props.tree) walk(n);
    return set;
  }, [expanded, filterActive, filterMatch, props.tree]);

  // ─── 키보드 네비게이션 (roving focus 패턴) ─────────────────────────────────
  const [focusedId, setFocusedId] = useState<string | null>(null);

  // 현재 보이는 순서대로 flat 한 행 목록 (filter / collapsed 반영).
  interface FlatRow {
    id: string;
    type: 'folder' | 'file';
    depth: number;
    parentId: string | null;
    hasChildren: boolean;
    isOpen: boolean;
  }
  const flatVisible = useMemo<FlatRow[]>(() => {
    const out: FlatRow[] = [];
    const walk = (
      nodes: ResponsePromptTreeNode[],
      depth: number,
      parentId: string | null,
    ): void => {
      for (const n of nodes) {
        if (filterActive && !isAncestorMatch(n, filterMatch)) continue;
        const isFolder = n.type === 'folder';
        const isOpen = effectiveExpanded.has(n.id);
        out.push({
          id: n.id,
          type: n.type,
          depth,
          parentId,
          hasChildren: n.children.length > 0,
          isOpen,
        });
        if (isFolder && isOpen) walk(n.children, depth + 1, n.id);
      }
    };
    walk(props.tree, 0, null);
    return out;
  }, [props.tree, effectiveExpanded, filterActive, filterMatch]);

  const onTreeKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (flatVisible.length === 0) return;
    const currentId = focusedId ?? flatVisible[0]?.id ?? null;
    const idx = flatVisible.findIndex((r) => r.id === currentId);
    if (idx === -1) return;
    const row = flatVisible[idx];
    if (!row) return;
    switch (e.key) {
      case 'ArrowDown': {
        const next = flatVisible[idx + 1];
        if (next) setFocusedId(next.id);
        e.preventDefault();
        break;
      }
      case 'ArrowUp': {
        const prev = flatVisible[idx - 1];
        if (prev) setFocusedId(prev.id);
        e.preventDefault();
        break;
      }
      case 'ArrowRight': {
        if (row.type === 'folder') {
          if (!row.isOpen && row.hasChildren) {
            setExpanded((prev) => {
              const next = new Set(prev);
              next.add(row.id);
              return next;
            });
          } else if (row.isOpen && row.hasChildren) {
            const first = flatVisible[idx + 1];
            if (first) setFocusedId(first.id);
          }
        }
        e.preventDefault();
        break;
      }
      case 'ArrowLeft': {
        if (row.type === 'folder' && row.isOpen) {
          setExpanded((prev) => {
            const next = new Set(prev);
            next.delete(row.id);
            return next;
          });
        } else if (row.parentId) {
          setFocusedId(row.parentId);
        }
        e.preventDefault();
        break;
      }
      case 'Enter':
      case ' ': {
        if (row.type === 'file') {
          props.onSelect(row.id);
        } else if (row.hasChildren) {
          setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(row.id)) next.delete(row.id);
            else next.add(row.id);
            return next;
          });
        }
        e.preventDefault();
        break;
      }
      case 'Delete': {
        props.onDelete(row.id);
        e.preventDefault();
        break;
      }
      case 'F2': {
        props.onRename(row.id);
        e.preventDefault();
        break;
      }
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border p-2">
        <Input
          placeholder="이름 / slug 검색…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="h-8 text-sm"
        />
        <div className="mt-2 flex gap-1">
          <Button
            type="button"
            variant="secondary"
            size="icon"
            className="flex-1 h-7"
            onClick={() => props.onCreate(null, 'folder')}
            title="폴더 추가"
            aria-label="폴더 추가"
          >
            <FolderPlus className="size-3.5" />
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="icon"
            className="flex-1 h-7"
            onClick={() => props.onCreate(null, 'file')}
            title="프롬프트 추가"
            aria-label="프롬프트 추가"
          >
            <FilePlus className="size-3.5" />
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="icon"
            className="flex-1 h-7"
            onClick={expandAll}
            disabled={allFolderIds.length === 0}
            title="전체 열기"
            aria-label="전체 열기"
          >
            <ChevronsUpDown className="size-3.5" />
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="icon"
            className="flex-1 h-7"
            onClick={collapseAll}
            disabled={expanded.size === 0}
            title="전체 닫기"
            aria-label="전체 닫기"
          >
            <ChevronsDownUp className="size-3.5" />
          </Button>
        </div>
      </div>
      <div
        className="flex-1 min-h-0 overflow-y-auto p-1 focus:outline-none"
        // tree role 컨테이너는 키보드 진입점 — 비대화형 div 에 tabIndex 0 + onKeyDown 로 roving focus 구현.
        // biome-ignore lint/a11y/noNoninteractiveTabindex: tree role 컨테이너는 키보드 네비 진입점이라 의도적으로 tabIndex 부여
        tabIndex={0}
        role="tree"
        onKeyDown={onTreeKeyDown}
        onFocus={() => {
          if (!focusedId && flatVisible[0]) setFocusedId(flatVisible[0].id);
        }}
      >
        {props.tree.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-foreground-muted">
            아직 등록된 프롬프트가 없습니다.
            <br />
            폴더 또는 프롬프트를 추가하세요.
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={pointerWithin}
            onDragStart={(e) => setDraggingId(String(e.active.id))}
            onDragCancel={() => setDraggingId(null)}
            onDragEnd={onDragEnd}
          >
            {draggingId ? <RootDropZone draggingId={draggingId} /> : null}
            {props.tree.map((n) => (
              <TreeRow
                key={n.id}
                node={n}
                depth={0}
                selectedFileId={props.selectedFileId}
                onSelect={(id) => {
                  setFocusedId(id);
                  props.onSelect(id);
                }}
                onCreate={props.onCreate}
                onRename={props.onRename}
                onDelete={props.onDelete}
                onDuplicate={props.onDuplicate}
                expanded={effectiveExpanded}
                setExpanded={setExpanded}
                filterMatch={filterMatch}
                filterActive={filterActive}
                draggingId={draggingId}
                focusedId={focusedId}
              />
            ))}
          </DndContext>
        )}
      </div>
    </div>
  );
}

/** 확인 모달 — 삭제용. */
export function ConfirmDeleteModal({
  open,
  onClose,
  name,
  onConfirm,
  isPending,
}: {
  open: boolean;
  onClose: () => void;
  name: string;
  onConfirm: () => void;
  isPending: boolean;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="삭제 확인"
      description={`"${name}" 을 삭제합니다. 하위 폴더/프롬프트와 모든 버전 이력이 함께 사라집니다.`}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isPending}>
            취소
          </Button>
          <Button type="button" variant="danger" onClick={onConfirm} disabled={isPending}>
            {isPending ? '삭제 중…' : '삭제'}
          </Button>
        </div>
      }
    >
      <div className="text-sm text-foreground-muted">이 작업은 되돌릴 수 없습니다.</div>
    </Modal>
  );
}
